import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { getSupplierStatuses } from '../../api'
import { JsonSupplierStatus, SupplierStatus, SupplierSubstatus } from '../../models'
import { useLocation } from 'react-router-dom'

interface FilterProps {
    updateFilters: (
        filterName: string,
        newValue: string,
        shouldDelete?: boolean,
        dependentFilterToClear?: string
    ) => void
}

interface LocationState {
    vendorCode?: string
}

export const SupplierFilters = (props: FilterProps) => {
    const { updateFilters } = props

    const [searchParams, setSearchParams] = useSearchParams()
    const [supplierStatuses, setSupplierStatuses] = useState<SupplierStatus[]>([])
    const [selectedParentStatus, setSelectedParentStatus] = useState<SupplierStatus>()
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const location = useLocation()
    const vendorCode = (location.state as LocationState)?.vendorCode

    const urlStatus = searchParams.get('status')
    const urlSubstatus = searchParams.get('supplier_substatus_code')

    useEffect(() => {
        setIsLoading(true)
        setError(null)
        getSupplierStatuses()
            .then((json) => {
                setSupplierStatuses(
                    json.data
                    // Remove "Recovery" from statuses so users cannot see which suppliers are in "Recovery"
                    // without using the Suppliers -> Recovery page (at MGMT's request). Recovery page has stricter
                    // user role requirements.
                    .filter((status: { code: number }) => status.code !== SupplierStatus.RECOVERY)
                    .map((status: JsonSupplierStatus) => new SupplierStatus(status))
                )
            })
            .catch((error) => {
                setError('Failed to load supplier statuses. Please try refreshing the page.')
            })
            .finally(() => setIsLoading(false))
    }, [])

    useEffect(() => {
        if (vendorCode) {
            updateFilters('vendorCode', vendorCode)
        }
    }, [])

    useEffect(() => {
        if (urlStatus && urlStatus !== '-1' && supplierStatuses.length > 0) {
            const parentStatus = supplierStatuses.find((status) => status.code === +urlStatus)
            setSelectedParentStatus(parentStatus)
        } else if (!urlStatus || urlStatus === '-1') {
            setSelectedParentStatus(undefined)
        }
    }, [urlStatus, supplierStatuses])

    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value

        const newSearchParams = new URLSearchParams(searchParams)

        if (value === '-1') {
            newSearchParams.delete('status')
            newSearchParams.delete('supplier_substatus_code')
        } else {
            newSearchParams.set('status', value)
            newSearchParams.delete('supplier_substatus_code')
        }
        if (newSearchParams.toString() !== searchParams.toString()) {
            setSearchParams(newSearchParams, { replace: true })
        }
        updateFilters('status', value ?? '', value === '-1', 'supplier_substatus_code')
        setSelectedParentStatus(supplierStatuses.find((status) => status.code === +value))
    }

    const handleSubstatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value
        const newSearchParams = new URLSearchParams(searchParams)
        if (value === '-1') {
            newSearchParams.delete('supplier_substatus_code')
        } else {
            newSearchParams.set('supplier_substatus_code', value)
        }
        if (newSearchParams.toString() !== searchParams.toString()) {
            setSearchParams(newSearchParams, { replace: true })
        }
        updateFilters('supplier_substatus_code', value ?? '', value === '-1')
    }

    if (error) {
        return (
            <div className="row g-2">
                <div className="col-12">
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="row g-2">
            <div className="col-lg-6 col-xl-4">
                <label htmlFor="supplierStatusFilter" className="form-label mb-1">
                    Supplier Status
                </label>
                <select
                    className="form-select form-select-sm"
                    id="supplierStatusFilter"
                    value={urlStatus || '-1'}
                    onChange={handleStatusChange}
                >
                    <option value="-1">{isLoading ? 'Loading...' : 'All'}</option>
                    {supplierStatuses.map((status: SupplierStatus) => (
                        <option key={status.code} value={status.code}>
                            {status.name}
                        </option>
                    ))}
                </select>
            </div>
            {selectedParentStatus?.substatuses &&
                selectedParentStatus?.substatuses.length > 0 && (
                    <div className="col-lg-6 col-xl-4">
                        <label htmlFor="supplierSubstatusFilter" className="form-label mb-1">
                            Supplier Substatus
                        </label>
                        <select
                            className="form-select form-select-sm"
                            id="supplierSubstatusFilter"
                            value={urlSubstatus || '-1'}
                            onChange={handleSubstatusChange}
                        >
                            <option value="-1">All</option>
                            {selectedParentStatus.substatuses.map(
                                (substatus: SupplierSubstatus) => (
                                    <option key={substatus.code} value={substatus.code}>
                                        {substatus.name}
                                    </option>
                                )
                            )}
                        </select>
                    </div>
                )}
        </div>
    )
}
